import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ticket,
  ShieldCheck,
  Ban,
  Sparkles,
  CalendarDays,
  Crown,
  Save,
  RefreshCw,
  ArrowLeft,
  Lock,
  Lightbulb,
  Star,
  UserPlus,
  Gavel,
  Gift,
  Dices,
  Archive,
  Server,
  Webhook,
  Database,
  ShieldAlert,
  ScrollText,
  Headphones,
  Bot,
  BarChart3,
  Vote,
  Bell,
  ExternalLink,
  LifeBuoy,
  Wand2,
  Zap,
  FileText,
  Languages,
  RotateCcw,
  TrendingUp,
} from "lucide-react";
import { api, GuildConfig } from "@/lib/api";
import { MultiSelect } from "@/components/MultiSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import { TicketsTab } from "@/pages/server-tabs/TicketsTab";
import { WhitelistTab } from "@/pages/server-tabs/WhitelistTab";
import { BlacklistTab } from "@/pages/server-tabs/BlacklistTab";
import { DailyTab } from "@/pages/server-tabs/DailyTab";
import { ReactionRolesTab } from "@/pages/server-tabs/ReactionRolesTab";
import { SuggestionsTab } from "@/pages/server-tabs/SuggestionsTab";
import { FeedbackTab } from "@/pages/server-tabs/FeedbackTab";
import { WelcomeTab } from "@/pages/server-tabs/WelcomeTab";
import { ModerationTab } from "@/pages/server-tabs/ModerationTab";
import { GiveawaysTab } from "@/pages/server-tabs/GiveawaysTab";
import { GamesCommunityTab } from "@/pages/server-tabs/GamesCommunityTab";
import { BackupTab, BackupList } from "@/pages/server-tabs/BackupTab";
import { FiveMTab } from "@/pages/server-tabs/FiveMTab";
import { WebhooksTab } from "@/pages/server-tabs/WebhooksTab";
import { SqlBackupTab } from "@/pages/server-tabs/SqlBackupTab";
import { SecurityTab } from "@/pages/server-tabs/SecurityTab";
import { LoggingTab } from "@/pages/server-tabs/LoggingTab";
import { VoiceRewardsTab } from "@/pages/server-tabs/VoiceRewardsTab";
import { LevelingTab } from "@/pages/server-tabs/LevelingTab";
import { CustomCommandsTab } from "@/pages/server-tabs/CustomCommandsTab";
import { StatChannelsTab } from "@/pages/server-tabs/StatChannelsTab";
import { PollsTab } from "@/pages/server-tabs/PollsTab";
import { SocialAlertsTab } from "@/pages/server-tabs/SocialAlertsTab";
import { MusicTab } from "@/pages/server-tabs/MusicTab";
import { AiArchitectTab } from "@/pages/server-tabs/AiArchitectTab";
import { AiHubTab } from "@/pages/server-tabs/AiHubTab";
import { EmbedBuilderTab } from "@/pages/server-tabs/EmbedBuilderTab";
import { PartnershipTab } from "@/pages/server-tabs/PartnershipTab";
import { GameAlertsTab } from "@/pages/server-tabs/GameAlertsTab";
import { InviteTrackerTab } from "@/pages/server-tabs/InviteTrackerTab";
import { StickyRolesTab } from "@/pages/server-tabs/StickyRolesTab";
import { LanguageTab } from "@/pages/server-tabs/LanguageTab";
import { useAuth } from "@/contexts/AuthContext";
import { PremiumGate } from "@/components/panel/PremiumGate";

const TABS = [
  { id: "ai_hub", label: "AI Hub & White-label", icon: Wand2, premium: true },
  { id: "tickets", label: "Ticket system", icon: Ticket, premium: false },
  { id: "ai_architect", label: "AI Server Architect", icon: Zap, premium: true },
  { id: "feedback", label: "Ticket feedback", icon: Star, premium: true },
  { id: "suggestions", label: "Suggestions", icon: Lightbulb, premium: false },
  { id: "polls", label: "Polls", icon: Vote, premium: false },
  { id: "social_alerts", label: "Social alerts", icon: Bell, premium: true },
  { id: "welcome", label: "Welcome & goodbye", icon: UserPlus, premium: false },
  { id: "moderation", label: "Moderation", icon: Gavel, premium: false },
  { id: "security", label: "Security & anti-nuke", icon: ShieldAlert, premium: false },
  { id: "logging", label: "Logging center", icon: ScrollText, premium: false },
  { id: "voice_rewards", label: "Voice rewards", icon: Headphones, premium: true },
  { id: "leveling", label: "Leveling & ranks", icon: TrendingUp, premium: true },
  { id: "stat_channels", label: "Stat channels", icon: BarChart3, premium: true },
  { id: "giveaways", label: "Giveaways", icon: Gift, premium: false },
  { id: "whitelist", label: "Applications / Whitelist", icon: ShieldCheck, premium: true },
  { id: "daily", label: "Daily messages", icon: CalendarDays, premium: false },
  { id: "custom_commands", label: "Custom commands", icon: Bot, premium: true },
  { id: "blacklist", label: "Blacklist", icon: Ban, premium: true },
  { id: "backup", label: "Server backup", icon: Archive, premium: true },
  { id: "fivem", label: "FiveM / RP server", icon: Server, premium: true },
  { id: "sql", label: "SQL backup", icon: Database, premium: true },
  { id: "webhooks", label: "Webhook scheduler", icon: Webhook, premium: true },
  { id: "rr", label: "Reaction roles", icon: Sparkles, premium: false },
  { id: "fun", label: "Games & economy", icon: Dices, premium: false },
  { id: "music", label: "Music bot", icon: Headphones, premium: false },
  { id: "embed_builder", label: "Embed Builder", icon: FileText, premium: false },
  { id: "partnership", label: "Partnerships", icon: UserPlus, premium: false },
  { id: "game_alerts", label: "Game Updates & Promos", icon: Bell, premium: false },
  { id: "invite_tracker", label: "Invite Tracker", icon: UserPlus, premium: false },
  { id: "sticky_roles", label: "Sticky Roles", icon: RotateCcw, premium: false },
  { id: "language", label: "Language", icon: Languages, premium: true },
];

const PANEL_TAB_STORAGE = (gid: string) => `pixelPanelTab:${gid}`;

/** Sidebar groups: module catalog without changing tab ids or behavior. */
const TAB_GROUPS: {
  label: string;
  description: string;
  tabIds: (typeof TABS)[number]["id"][];
}[] = [
  {
    label: "Essentials",
    description: "First setup, roles and music configuration.",
    tabIds: ["ai_hub", "ai_architect", "welcome", "rr", "daily", "custom_commands", "language"],
  },
  {
    label: "Support & tickets",
    description: "Tickets, staff feedback and server suggestions.",
    tabIds: ["tickets", "feedback", "suggestions", "polls", "embed_builder"],
  },
  {
    label: "Moderation & security",
    description: "Protection, audit logs, access control and staff tools.",
    tabIds: ["security", "moderation", "logging", "blacklist", "whitelist"],
  },
  {
    label: "Server management",
    description: "Backups, RP server tools, SQL and scheduled webhooks.",
    tabIds: ["backup", "fivem", "sql", "webhooks"],
  },
  {
    label: "Community growth",
    description: "Rewards and events that keep members active.",
    tabIds: ["giveaways", "voice_rewards", "leveling", "stat_channels", "social_alerts", "partnership", "game_alerts", "invite_tracker", "sticky_roles"],
  },
  {
    label: "Games & economy",
    description:
      "Casino games, economy tools — no Premium required; available on every server.",
    tabIds: ["fun", "music"],
  },
];

function canOpenTab(
  id: (typeof TABS)[number]["id"],
  premium: boolean,
): boolean {
  const t = TABS.find((x) => x.id === id);
  return Boolean(t && (!t.premium || premium));
}

const DEFAULT_LOGGING: GuildConfig["logging"] = {
  enabled: false,
  default_channel_id: "",
  message_channel_id: "",
  member_channel_id: "",
  moderation_channel_id: "",
  server_channel_id: "",
  voice_channel_id: "",
  log_message_delete: true,
  log_message_edit: true,
  log_member_join: true,
  suppress_member_join_when_welcome_enabled: true,
  log_member_leave: true,
  log_member_update: true,
  log_ban: true,
  log_unban: true,
  log_role_create: true,
  log_role_delete: true,
  log_role_update: true,
  log_channel_create: true,
  log_channel_delete: true,
  log_channel_update: true,
  log_voice: false,
  ignore_bot_messages: true,
  ignored_channel_ids: [],
};

const DEFAULT_LANGUAGE: GuildConfig["language"] = {
  enabled: false,
  overrides: {},
};

const DEFAULT_VOICE_REWARDS: GuildConfig["voice_rewards"] = {
  enabled: false,
  log_channel_id: "",
  ignored_channel_ids: [],
  tiers: [],
};

const DEFAULT_LEVELING: GuildConfig["leveling"] = {
  enabled: false,
  xp_min: 15,
  xp_max: 25,
  cooldown_seconds: 60,
  level_up_message_enabled: true,
  level_up_channel_id: "",
  level_up_message: "🎉 {user} reached **Level {level}**!",
  ignored_channel_ids: [],
  ignored_role_ids: [],
  level_roles: [],
};

const DEFAULT_CUSTOM_COMMANDS: GuildConfig["custom_commands"] = {
  enabled: false,
  prefix: "!",
  log_channel_id: "",
  commands: [],
  auto_responses: [],
};

const DEFAULT_STAT_CHANNELS: GuildConfig["stat_channels"] = {
  enabled: false,
  update_minutes: 15,
  channels: [],
};

const DEFAULT_POLLS: GuildConfig["polls"] = {
  enabled: false,
  allowed_channel_ids: [],
  manager_role_ids: [],
  default_duration_hours: 24,
  max_options: 5,
  allow_multiple_default: false,
  anonymous_default: true,
  show_live_results: true,
  show_poll_duration: true,
  embed_color: "#5865F2",
  log_channel_id: "",
};

const DEFAULT_BOT_PERSONALIZER: GuildConfig["bot_personalizer"] = {
  enabled: false,
  nickname: "",
  embed_author_name: "Areon",
  embed_footer: "Powered by Areon",
  embed_color: "#5865F2",
  status_text: "",
  keep_orbit_branding: true,
};

const DEFAULT_SOCIAL_ALERTS: GuildConfig["social_alerts"] = {
  enabled: false,
  check_minutes: 10,
  alerts: [],
};

const DEFAULT_MUSIC: GuildConfig["music"] = {
  enabled: true,
  orbit_prefix: ".",
  orbit_command_mode: "prefix",
  allowed_channel_ids: [],
  dj_role_ids: [],
  max_queue: 50,
  default_volume: 50,
};

const DEFAULT_SECURITY: GuildConfig["security"] = {
  enabled: false,
  log_channel_id: "",
  manager_role_ids: [],
  exempt_role_ids: [],
  exempt_channel_ids: [],
  anti_spam: {
    enabled: true,
    max_messages: 6,
    interval_seconds: 8,
    action: "timeout",
    timeout_minutes: 10,
    delete_messages: true,
    duplicate_enabled: true,
    duplicate_threshold: 4,
    mention_limit: 8,
    caps_enabled: false,
    caps_percent: 80,
    caps_min_length: 20,
  },
  anti_invite: {
    enabled: true,
    action: "delete",
    allow_own_invites: false,
    delete_message: true,
  },
  automod: {
    enabled: true,
    blocked_words: [],
    blocked_patterns: [],
    match_mode: "contains",
    case_sensitive: false,
    delete_message: true,
    action: "warn",
    timeout_minutes: 10,
    dm_user: true,
  },
  anti_nuke: {
    enabled: true,
    window_seconds: 60,
    max_channel_deletes: 3,
    max_role_deletes: 3,
    max_bans: 5,
    action: "ban",
  },
  anti_raid: {
    enabled: false,
    window_seconds: 10,
    max_joins: 10,
    cooldown_seconds: 120,
    action: "alert",
    timeout_minutes: 60,
    min_account_age_minutes: 0,
  },
  verification: {
    enabled: false,
    unverified_role_id: "",
    verified_role_id: "",
    skip_autorole_until_verified: true,
  },
  trap_channel: {
    enabled: false,
    channel_ids: [],
    timeout_minutes: 10080,
  },
};

function normalizeServerConfig(raw: GuildConfig): GuildConfig {
  const security = raw.security ?? DEFAULT_SECURITY;
  return {
    ...raw,
    backup: raw.backup ?? {
      enabled: false,
      log_channel_id: "",
      create_role_ids: [],
    },
    dashboard: raw.dashboard ?? { premium_delegate_role_ids: [] },
    fivem: raw.fivem ?? {
      enabled: false,
      server_name: "",
      connect_url: "",
      rules_channel_id: "",
      wl_channel_id: "",
      status_channel_id: "",
      status_message: "Connect: {connect}\nWhitelist: {wl_channel}",
      auto_reply_enabled: true,
      auto_reply_keywords: ["fivem", "connect", "wl", "whitelist"],
      auto_reply_message: "For whitelist, open {wl_channel}. Connect: {connect}",
    },
    webhooks: raw.webhooks ?? { enabled: false, jobs: [] },
    invite_tracker: raw.invite_tracker ?? {
      enabled: false,
      log_channel_id: "",
      join_message: "{member} joined! Invited by {inviter} ({invites} invites)",
      leave_message: "{member} left! Invited by {inviter} ({invites} invites)",
      fake_threshold_days: 3
    },
    sticky_roles: raw.sticky_roles ?? { enabled: false, role_ids: [] },
    sql_backup: raw.sql_backup ?? {
      enabled: false,
      db_type: "mysql",
      host: "localhost",
      port: 3306,
      database: "",
      username: "",
      password: "",
      webhook_url: "",
      interval_hours: 24,
      retention: 7,
      log_channel_id: "",
    },
    logging: { ...DEFAULT_LOGGING, ...(raw.logging ?? {}) },
    language: {
      ...DEFAULT_LANGUAGE,
      ...(raw.language ?? {}),
      overrides: { ...(raw.language?.overrides ?? {}) },
    },
    custom_commands: {
      ...DEFAULT_CUSTOM_COMMANDS,
      ...(raw.custom_commands ?? {}),
      commands: Array.isArray(raw.custom_commands?.commands)
        ? raw.custom_commands.commands
        : [],
      auto_responses: Array.isArray(raw.custom_commands?.auto_responses)
        ? raw.custom_commands.auto_responses
        : [],
    },
    stat_channels: {
      ...DEFAULT_STAT_CHANNELS,
      ...(raw.stat_channels ?? {}),
      channels: Array.isArray(raw.stat_channels?.channels)
        ? raw.stat_channels.channels
        : [],
    },
    polls: {
      ...DEFAULT_POLLS,
      ...(raw.polls ?? {}),
      allowed_channel_ids: Array.isArray(raw.polls?.allowed_channel_ids)
        ? raw.polls.allowed_channel_ids
        : [],
      manager_role_ids: Array.isArray(raw.polls?.manager_role_ids)
        ? raw.polls.manager_role_ids
        : [],
    },
    bot_personalizer: {
      ...DEFAULT_BOT_PERSONALIZER,
      ...(raw.bot_personalizer ?? {}),
    },
    social_alerts: {
      ...DEFAULT_SOCIAL_ALERTS,
      ...(raw.social_alerts ?? {}),
      alerts: Array.isArray(raw.social_alerts?.alerts)
        ? raw.social_alerts.alerts
        : [],
    },
    voice_rewards: {
      ...DEFAULT_VOICE_REWARDS,
      ...(raw.voice_rewards ?? {}),
      ignored_channel_ids: Array.isArray(raw.voice_rewards?.ignored_channel_ids)
        ? raw.voice_rewards.ignored_channel_ids
        : [],
      tiers: Array.isArray(raw.voice_rewards?.tiers) ? raw.voice_rewards.tiers : [],
    },
    leveling: {
      ...DEFAULT_LEVELING,
      ...(raw.leveling ?? {}),
      ignored_channel_ids: Array.isArray(raw.leveling?.ignored_channel_ids)
        ? raw.leveling.ignored_channel_ids
        : [],
      ignored_role_ids: Array.isArray(raw.leveling?.ignored_role_ids)
        ? raw.leveling.ignored_role_ids
        : [],
      level_roles: Array.isArray(raw.leveling?.level_roles) ? raw.leveling.level_roles : [],
    },
    security: {
      ...DEFAULT_SECURITY,
      ...security,
      anti_spam: { ...DEFAULT_SECURITY.anti_spam, ...(security.anti_spam ?? {}) },
      anti_invite: { ...DEFAULT_SECURITY.anti_invite, ...(security.anti_invite ?? {}) },
      automod: { ...DEFAULT_SECURITY.automod, ...(security.automod ?? {}) },
      anti_nuke: { ...DEFAULT_SECURITY.anti_nuke, ...(security.anti_nuke ?? {}) },
      anti_raid: { ...DEFAULT_SECURITY.anti_raid, ...(security.anti_raid ?? {}) },
      verification: { ...DEFAULT_SECURITY.verification, ...(security.verification ?? {}) },
      trap_channel: { ...DEFAULT_SECURITY.trap_channel, ...(security.trap_channel ?? {}) },
    },
    blacklist: {
      ...(raw.blacklist ?? {
        enabled: false,
        role_id: "",
        member_role_id: "",
        log_channel_id: "",
        mod_role_ids: [],
        admin_role_ids: [],
      }),
      entries: Array.isArray(raw.blacklist?.entries) ? raw.blacklist!.entries : [],
    },
    music: {
      ...DEFAULT_MUSIC,
      ...(raw.music ?? {}),
      orbit_prefix: (() => {
        const rm = raw.music as Record<string, unknown> | undefined;
        if (typeof rm?.orbit_prefix === "string" && rm.orbit_prefix) return rm.orbit_prefix as string;
        if (typeof rm?.rio_prefix === "string" && rm.rio_prefix) return rm.rio_prefix as string;
        return DEFAULT_MUSIC.orbit_prefix;
      })(),
      orbit_command_mode: (() => {
        const rm = raw.music as Record<string, unknown> | undefined;
        const v = String(
          rm?.orbit_command_mode ?? rm?.rio_command_mode ?? rm?.harmonica_command_mode ?? "prefix",
        ).toLowerCase();
        return v === "slash" || v === "prefix" || v === "both"
          ? (v as "slash" | "prefix" | "both")
          : DEFAULT_MUSIC.orbit_command_mode;
      })(),
      allowed_channel_ids: Array.isArray(raw.music?.allowed_channel_ids)
        ? raw.music.allowed_channel_ids
        : DEFAULT_MUSIC.allowed_channel_ids,
      dj_role_ids: Array.isArray(raw.music?.dj_role_ids)
        ? raw.music.dj_role_ids
        : DEFAULT_MUSIC.dj_role_ids,
    },
    embed_builder: raw.embed_builder ?? {
      enabled: false,
      embeds: [],
    },
  };
}

export function ServerPanel() {
  const { guildId } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [active, setActive] = useState<(typeof TABS)[number]["id"]>("fun");
  const [config, setConfig] = useState<GuildConfig | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["server", guildId],
    queryFn: () => api.server(guildId!),
    enabled: !!guildId,
  });

  const { data: pubStats } = useQuery({
    queryKey: ["publicStats"],
    queryFn: () => api.publicStats(),
    staleTime: 120_000,
  });

  const selectTab = useCallback(
    (id: (typeof TABS)[number]["id"]) => {
      setActive(id);
      if (guildId) {
        try {
          localStorage.setItem(PANEL_TAB_STORAGE(guildId), id);
        } catch {
          /* private mode */
        }
      }
    },
    [guildId],
  );

  useEffect(() => {
    if (data) {
      setConfig(normalizeServerConfig(data.config));
      setDirty(false);
    }
  }, [data]);

  useEffect(() => {
    if (!data || !guildId) return;
    const p = data.premium;
    let next: (typeof TABS)[number]["id"] = p ? "tickets" : "fun";
    try {
      const raw = localStorage.getItem(PANEL_TAB_STORAGE(guildId));
      const known = raw && TABS.some((t) => t.id === raw);
      if (known && canOpenTab(raw as (typeof TABS)[number]["id"], p)) {
        next = raw as (typeof TABS)[number]["id"];
      } else if (known && !canOpenTab(raw as (typeof TABS)[number]["id"], p)) {
        next = p ? "tickets" : "fun";
      }
    } catch {
      /* ignore */
    }
    setActive((cur) => (cur === next ? cur : next));
  }, [guildId, data?.premium, data]);

  const saveMut = useMutation({
    mutationFn: (cfg: GuildConfig) => api.saveServer(guildId!, cfg),
    onSuccess: (resp) => {
      setConfig(normalizeServerConfig(resp.config));
      setDirty(false);
      toast.success("Settings saved.");
      qc.invalidateQueries({ queryKey: ["server", guildId] });
    },
    onError: (e: Error) => {
      toast.error(e.message || "Failed to save.");
    },
  });

  const refreshMut = useMutation({
    mutationFn: () => api.refresh(guildId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server", guildId] });
      toast.success("Data refreshed from Discord.");
    },
  });

  if (error) {
    const e = error as Error & { data?: { invite_url?: string } };
    return (
      <Card className="p-8 text-center border-destructive/40 space-y-4 max-w-lg mx-auto shadow-2xl">
        <div className="h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-2">
          <ShieldAlert className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="text-xl font-bold">Server Not Found</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          The <strong>main Areon bot</strong> must be active on this server before the panel can be loaded. 
          If you just added the bot, please try refreshing.
        </p>
        
        <div className="flex flex-col gap-2 pt-2">
          {e.data?.invite_url && (
            <a href={e.data.invite_url} className="block">
              <Button className="w-full bg-primary hover:bg-primary/90">
                <Bot className="mr-2 h-4 w-4" />
                Add bot to this server
              </Button>
            </a>
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => qc.invalidateQueries({ queryKey: ["server", guildId] })}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry Connection
          </Button>
          <Link to="/dashboard" className="block">
            <Button variant="ghost" className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to server list
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (isLoading || !config || !data) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  const { guild, premium, can_buy_premium, is_guild_owner, channels, roles } = data;

  function updateConfig<K extends keyof GuildConfig>(
    key: K,
    value: GuildConfig[K],
  ) {
    setConfig((c) => (c ? { ...c, [key]: value } : c));
    setDirty(true);
  }

  function save() {
    if (config) saveMut.mutate(config);
  }

  return (
    <div className="flex flex-col lg:block min-h-screen pb-20 lg:w-screen lg:relative lg:left-1/2 lg:right-1/2 lg:-ml-[50vw] lg:-mr-[50vw]">
      <Toaster position="top-right" theme="dark" richColors />

      {/* Sticky Save Bar — Discord style, centered on viewport via portal */}
      {createPortal(
        <AnimatePresence>
          {dirty && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 40 }}
              className="fixed inset-x-0 bottom-6 z-[9999] flex justify-center px-4 pointer-events-none"
            >
              <div className="flex items-center gap-4 px-5 py-3 rounded-2xl bg-[#1e1f22] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] min-w-[340px] pointer-events-auto">
                <div className="flex items-center gap-2 flex-1">
                  <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  <span className="text-sm font-medium text-white/80 whitespace-nowrap">Careful — you have unsaved changes!</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    className="text-xs text-white/60 hover:text-white transition-colors px-2 py-1"
                    onClick={() => { setConfig(normalizeServerConfig(data.config)); setDirty(false); }}
                    disabled={saveMut.isPending}
                  >
                    Reset
                  </button>
                  <Button
                    size="sm"
                    className="h-8 px-4 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white border-0"
                    onClick={save}
                    disabled={saveMut.isPending}
                  >
                    <Save className="h-3.5 w-3.5" />
                    {saveMut.isPending ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* Mobile Sticky Header */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-background/80 backdrop-blur-md border-b p-4 -mx-4 sm:-mx-6 mb-4 shadow-sm">
        <div className="flex items-center gap-3">
          {guild.icon_url ? (
            <img src={guild.icon_url} className="h-8 w-8 rounded-lg" alt="" />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center font-bold text-primary">
              {guild.name[0]}
            </div>
          )}
          <span className="font-bold truncate max-w-[150px]">{guild.name}</span>
        </div>
        <div className="flex items-center gap-2">
           <Button variant="outline" size="icon" onClick={() => save()} disabled={!dirty || saveMut.isPending} className={dirty ? "border-primary text-primary" : ""}>
             <Save className="h-4 w-4" />
           </Button>
           <Button variant="secondary" size="icon" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="lg:hidden">
             <RefreshCw className="h-4 w-4" onClick={() => refreshMut.mutate()} />
           </Button>
        </div>
      </div>

       {/* Sidebar — pinned to far left on desktop (full-width layout) */}
      <aside className="w-full space-y-5 lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:w-[264px] lg:h-screen lg:pt-20 lg:pb-6 lg:px-3 lg:border-r lg:border-border/40 lg:bg-background lg:overflow-y-auto no-scrollbar lg:z-30">
        {/* Guild Info Card — clean */}
        <Card className="p-4 border-border/50 bg-card/60">
          <div className="flex items-center gap-3">
            {guild.icon_url ? (
              <img src={guild.icon_url} className="h-11 w-11 rounded-xl" alt="" />
            ) : (
              <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center font-semibold text-lg text-primary">
                {guild.name[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-sm truncate text-foreground">{guild.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                {premium ? (
                  <Badge variant="premium" className="px-1.5 py-0 text-[10px] font-medium">
                    <Crown className="h-2.5 w-2.5 mr-0.5" /> Premium
                  </Badge>
                ) : (
                  <span className="text-[11px] text-muted-foreground">Basic plan</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-5 pb-12">
          {TAB_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1.5">
              <h3 className="px-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/50">
                {group.label}
              </h3>
              <div className="space-y-0.5">
                {group.tabIds
                  .map((id) => TABS.find((t) => t.id === id))
                  .filter(Boolean)
                  .filter((t) => {
                    if (t!.id === "invite_tracker" || t!.id === "sticky_roles") {
                      return Boolean(user?.is_beta_tester);
                    }
                    return true;
                  })
                  .map((t) => {
                    const tab = t!;
                    const Icon = tab.icon;
                    const locked = tab.premium && !premium;
                    const isActive = active === tab.id;
                    return (
                      <button
                        type="button"
                        key={tab.id}
                        onClick={() => selectTab(tab.id)}
                        className={`group w-full flex items-center gap-2.5 pl-2.5 pr-2 py-2 rounded-lg text-sm transition-colors duration-150 relative ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-accent/40 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {isActive && (
                          <motion.div layoutId="active-tab-indicator" className="absolute left-0 w-0.5 h-5 bg-primary rounded-full" />
                        )}
                        <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground/70 group-hover:text-foreground"}`} />
                        <span className="flex-1 text-left font-medium whitespace-nowrap overflow-hidden text-ellipsis text-[13px]">{tab.label}</span>
                        {tab.premium ? (
                          <Crown className={`h-3 w-3 ${locked ? "text-muted-foreground/30" : "text-amber-400/80"}`} />
                        ) : null}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </aside>

        {/* Content */}
        <div className="min-w-0 flex-1 lg:ml-[264px] lg:pl-8 lg:pr-8 xl:pr-12 lg:pt-8">
          {/* Content Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground transition-colors">{guild.name}</Link>
              <span className="text-muted-foreground/40">/</span>
              <span className="text-foreground font-medium">
                {TABS.find(t => t.id === active)?.label ?? active}
              </span>
            </div>
            <div className="hidden lg:flex items-center gap-1.5">
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 text-muted-foreground hover:text-foreground" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
                <RefreshCw className={`h-3.5 w-3.5 ${refreshMut.isPending ? "animate-spin" : ""}`} />
                Sync
              </Button>
              <Button
                size="sm"
                variant={dirty ? "default" : "outline"}
                className="h-8 text-xs gap-1.5"
                onClick={save}
                disabled={!dirty || saveMut.isPending}
              >
                <Save className="h-3.5 w-3.5" />
                {saveMut.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              {active === "tickets" && (
                <TicketsTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("tickets", v)}
                  premium={premium}
                />
              )}
              {active === "ai_hub" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="AI Hub & White-label"
                >
                  <AiHubTab
                    guildId={guildId!}
                    config={config}
                    channels={channels}
                    onChange={(v) => {
                      if (v.ai_faq) updateConfig("ai_faq", v.ai_faq);
                      if (v.custom_bot) updateConfig("custom_bot", v.custom_bot);
                    }}
                  />
                </PremiumGate>
              )}
              {active === "ai_architect" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="AI Server Architect"
                >
                  <AiArchitectTab
                    config={config.ai_architect}
                    onChange={(v) => updateConfig("ai_architect", v)}
                  />
                </PremiumGate>
              )}
              {active === "feedback" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Ticket Feedback"
                >
                  <FeedbackTab
                    config={config}
                    channels={channels}
                    onChange={(v) => updateConfig("ticket_feedback", v)}
                  />
                </PremiumGate>
              )}
              {active === "suggestions" && (
                <SuggestionsTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("suggestions", v)}
                />
              )}
              {active === "polls" && (
                <PollsTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("polls", v)}
                />
              )}
              {active === "social_alerts" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Social Alerts"
                >
                  <SocialAlertsTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("social_alerts", v)}
                  />
                </PremiumGate>
              )}
              {active === "welcome" && (
                <WelcomeTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onWelcome={(v) => updateConfig("welcome", v)}
                  onGoodbye={(v) => updateConfig("goodbye", v)}
                  onAutorole={(v) => updateConfig("autorole", v)}
                />
              )}
              {active === "moderation" && (
                <ModerationTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("moderation", v)}
                />
              )}
              {active === "security" && (
                <SecurityTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("security", v)}
                />
              )}
              {active === "logging" && (
                <LoggingTab
                  config={config}
                  channels={channels}
                  onChange={(v) => updateConfig("logging", v)}
                />
              )}
              {active === "giveaways" && (
                <GiveawaysTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("giveaways", v)}
                />
              )}
              {active === "voice_rewards" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Voice Rewards"
                >
                  <VoiceRewardsTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("voice_rewards", v)}
                  />
                </PremiumGate>
              )}
              {active === "leveling" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Leveling & Ranks"
                >
                  <LevelingTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("leveling", v)}
                  />
                </PremiumGate>
              )}
              {active === "stat_channels" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Stat Channels"
                >
                  <StatChannelsTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("stat_channels", v)}
                  />
                </PremiumGate>
              )}
              {active === "whitelist" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Whitelist"
                >
                  <WhitelistTab
                    guildId={guildId!}
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("whitelist", v)}
                  />
                </PremiumGate>
              )}
              {active === "daily" && (
                <DailyTab
                  config={config}
                  channels={channels}
                  onChange={(v) => updateConfig("daily", v)}
                />
              )}
              {active === "custom_commands" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Custom Commands"
                >
                  <CustomCommandsTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("custom_commands", v)}
                  />
                </PremiumGate>
              )}
              {active === "blacklist" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Blacklist"
                >
                  <BlacklistTab
                    config={config}
                    channels={channels}
                    roles={roles}
                    onChange={(v) => updateConfig("blacklist", v)}
                  />
                </PremiumGate>
              )}
              {active === "backup" && (
                <div className="space-y-4">
                  {/* Seznam obstojecih backupov je viden ne glede na premium */}
                  <BackupList guildId={guildId!} />
                  <PremiumGate
                    premium={premium}
                    canBuy={can_buy_premium}
                    guildId={guildId!}
                    featureName="Backup"
                  >
                    <BackupTab
                      config={config}
                      roles={roles}
                      onChange={(v) => updateConfig("backup", v)}
                    />
                  </PremiumGate>
                </div>
              )}
              {active === "sql" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="SQL Backup"
                >
                  <SqlBackupTab
                    config={config}
                    channels={channels}
                    onChange={(v) => updateConfig("sql_backup", v)}
                  />
                </PremiumGate>
              )}
              {active === "fivem" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="FiveM"
                >
                  <FiveMTab
                    config={config}
                    channels={channels}
                    onChange={(v) => updateConfig("fivem", v)}
                  />
                </PremiumGate>
              )}
              {active === "webhooks" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Webhooks"
                >
                  <WebhooksTab
                    config={config}
                    channels={channels}
                    onChange={(v) => updateConfig("webhooks", v)}
                  />
                </PremiumGate>
              )}
              {active === "rr" && (
                <ReactionRolesTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("reaction_roles", v)}
                />
              )}
              {active === "music" && (
                <MusicTab
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("music", v)}
                />
              )}
              {active === "fun" && (
                <GamesCommunityTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  onChangeGames={(v) => updateConfig("games", v)}
                />
              )}
              {active === "embed_builder" && (
                <EmbedBuilderTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  onChange={(v) => updateConfig("embed_builder", v)}
                />
              )}
              {active === "partnership" && (
                <PartnershipTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  roles={roles}
                  onChange={(v) => updateConfig("partnership", v)}
                />
              )}
              {active === "game_alerts" && (
                <GameAlertsTab
                  config={config}
                  channels={channels}
                  onChange={(v) => updateConfig("game_alerts", v)}
                />
              )}
              {active === "invite_tracker" && user?.is_beta_tester && (
                <InviteTrackerTab
                  guildId={guildId!}
                  config={config}
                  channels={channels}
                  onChange={(v) => updateConfig("invite_tracker", v)}
                />
              )}
              {active === "sticky_roles" && user?.is_beta_tester && (
                <StickyRolesTab
                  config={config}
                  roles={roles}
                  onChange={(v) => updateConfig("sticky_roles", v)}
                />
              )}
              {active === "language" && (
                <PremiumGate
                  premium={premium}
                  canBuy={can_buy_premium}
                  guildId={guildId!}
                  featureName="Language"
                >
                  <LanguageTab
                    config={config}
                    onChange={(v) => updateConfig("language", v)}
                  />
                </PremiumGate>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
}

